export const RANKING_VIEW_LIMITS = {
    PRIME_PICKS: 25,
    ESSENTIALS: 50,
    TOP_100: 100
};
function isNonEmptyString(value) {
    return typeof value === 'string' && value.trim().length > 0;
}
export function isValidCanonicalRankingPlacement(value) {
    if (!value || typeof value !== 'object')
        return false;
    const placement = value;
    if (!isNonEmptyString(placement.laneId) || !isNonEmptyString(placement.genre))
        return false;
    if (placement.laneType !== 'genre' && placement.laneType !== 'subgenre')
        return false;
    if (typeof placement.rank !== 'number' || !Number.isInteger(placement.rank) || placement.rank < 1)
        return false;
    const hasSubgenre = isNonEmptyString(placement.subgenre);
    if (placement.laneType === 'genre')
        return !hasSubgenre;
    return hasSubgenre;
}
export function getValidRankingPlacements(asset) {
    const seenLaneIds = new Set();
    return asset.rankingPlacements.filter((placement) => {
        if (!isValidCanonicalRankingPlacement(placement) || seenLaneIds.has(placement.laneId))
            return false;
        seenLaneIds.add(placement.laneId);
        return true;
    });
}
export function getCanonicalRank(placement) {
    return Number.isInteger(placement.rank) && placement.rank >= 1 ? placement.rank : null;
}
export function getPlacementsForLane(asset, laneId) {
    return getValidRankingPlacements(asset).filter((placement) => placement.laneId === laneId);
}
export function getPlacementForLane(asset, laneId) {
    return getPlacementsForLane(asset, laneId)[0] || null;
}
export function getAssetsForLane(assets, laneId) {
    const seenAssetGroupIds = new Set();
    return assets
        .filter((asset) => {
        if (seenAssetGroupIds.has(asset.assetGroupId) || !getPlacementForLane(asset, laneId))
            return false;
        seenAssetGroupIds.add(asset.assetGroupId);
        return true;
    })
        .sort((a, b) => {
        const aRank = getCanonicalRank(getPlacementForLane(a, laneId)) || Number.MAX_SAFE_INTEGER;
        const bRank = getCanonicalRank(getPlacementForLane(b, laneId)) || Number.MAX_SAFE_INTEGER;
        return aRank - bRank;
    });
}
export function isPrimePick(placement) {
    const rank = getCanonicalRank(placement);
    return rank !== null && rank <= RANKING_VIEW_LIMITS.PRIME_PICKS;
}
export function isEssential(placement) {
    const rank = getCanonicalRank(placement);
    return rank !== null && rank <= RANKING_VIEW_LIMITS.ESSENTIALS;
}
export function isTop100(placement) {
    const rank = getCanonicalRank(placement);
    return rank !== null && rank <= RANKING_VIEW_LIMITS.TOP_100;
}
export function isYearHit(placement, releaseYear) {
    const rank = getCanonicalRank(placement);
    return rank !== null && rank > RANKING_VIEW_LIMITS.TOP_100 && Number.isInteger(releaseYear);
}
export function getYearHitsReleaseYear(placement, releaseYear) {
    return isYearHit(placement, releaseYear) ? releaseYear : null;
}
export function getPlacementViewMembership(placement, releaseYear) {
    const rank = getCanonicalRank(placement);
    if (rank === null)
        return [];
    if (rank <= RANKING_VIEW_LIMITS.PRIME_PICKS)
        return ['PRIME_PICKS', 'ESSENTIALS', 'TOP_100'];
    if (rank <= RANKING_VIEW_LIMITS.ESSENTIALS)
        return ['ESSENTIALS', 'TOP_100'];
    if (rank <= RANKING_VIEW_LIMITS.TOP_100)
        return ['TOP_100'];
    return isYearHit(placement, releaseYear) ? ['YEAR_HITS'] : [];
}
export function getAssetViewMembership(asset, laneId) {
    const placement = getPlacementForLane(asset, laneId);
    return placement ? getPlacementViewMembership(placement, asset.releaseYear) : [];
}
export function isInRankingView(placement, view, releaseYear) {
    if (view === 'PRIME_PICKS')
        return isPrimePick(placement);
    if (view === 'ESSENTIALS')
        return isEssential(placement);
    if (view === 'TOP_100')
        return isTop100(placement);
    return isYearHit(placement, releaseYear);
}
export function filterAssetsForView(assets, view, laneId, releaseYear) {
    return getAssetsForLane(assets, laneId).filter((asset) => {
        const placement = getPlacementForLane(asset, laneId);
        return Boolean(placement && isInRankingView(placement, view, asset.releaseYear) &&
            (releaseYear === undefined || asset.releaseYear === releaseYear));
    });
}
export function discoverYearHitsYears(assets, laneId) {
    return [...new Set(getAssetsForLane(assets, laneId)
            .map((asset) => {
            const placement = getPlacementForLane(asset, laneId);
            return placement ? getYearHitsReleaseYear(placement, asset.releaseYear) : null;
        })
            .filter((year) => year !== null))].sort((a, b) => b - a);
}
export function groupYearHits(assets, laneId) {
    const groups = new Map();
    for (const asset of filterAssetsForView(assets, 'YEAR_HITS', laneId)) {
        const year = asset.releaseYear;
        if (typeof year !== 'number' || !Number.isInteger(year))
            continue;
        groups.set(year, [...(groups.get(year) || []), asset]);
    }
    return groups;
}
export function getCanonicalLaneDescriptors(assets) {
    const genreLaneIds = new Map();
    for (const asset of assets) {
        for (const placement of getValidRankingPlacements(asset)) {
            if (placement.laneType !== 'genre')
                continue;
            const existing = genreLaneIds.get(placement.genre);
            genreLaneIds.set(placement.genre, existing === undefined || existing === placement.laneId ? placement.laneId : null);
        }
    }
    const lanes = new Map();
    for (const asset of assets) {
        for (const placement of getValidRankingPlacements(asset)) {
            const parentLaneId = placement.laneType === 'subgenre' ? genreLaneIds.get(placement.genre) || null : null;
            if (parentLaneId && !lanes.has(parentLaneId)) {
                lanes.set(parentLaneId, {
                    laneId: parentLaneId,
                    laneType: 'genre',
                    genre: placement.genre,
                    subgenre: null,
                    parentLaneId: null
                });
            }
            if (lanes.has(placement.laneId))
                continue;
            lanes.set(placement.laneId, {
                laneId: placement.laneId,
                laneType: placement.laneType,
                genre: placement.genre,
                subgenre: placement.subgenre || null,
                parentLaneId
            });
        }
    }
    return [...lanes.values()].sort((a, b) => {
        if (a.laneType !== b.laneType)
            return a.laneType === 'genre' ? -1 : 1;
        return (a.subgenre || a.genre).localeCompare(b.subgenre || b.genre);
    });
}
/**
 * Builds the customer-facing Prime Picks discovery shape from canonical data.
 * The carousel intentionally uses only ranks 1–3; the full 25-item collection
 * remains available through the existing catalog view.
 */
export function buildPrimePicksCarouselCategories(assets) {
    const availableAssets = assets.filter((asset) => asset.availability.trim().toLowerCase() === 'available');
    const entriesByLane = new Map();
    for (const asset of availableAssets) {
        for (const placement of getValidRankingPlacements(asset)) {
            if (placement.rank > 3)
                continue;
            const laneEntries = entriesByLane.get(placement.laneId) || new Map();
            if (!laneEntries.has(asset.assetGroupId)) {
                laneEntries.set(asset.assetGroupId, { asset, rank: placement.rank });
            }
            entriesByLane.set(placement.laneId, laneEntries);
        }
    }
    return getCanonicalLaneDescriptors(availableAssets).flatMap((lane) => {
        const entries = [...(entriesByLane.get(lane.laneId)?.values() || [])]
            .sort((a, b) => a.rank - b.rank || a.asset.assetGroupId.localeCompare(b.asset.assetGroupId));
        if (entries.length === 0)
            return [];
        return [{
                laneId: lane.laneId,
                laneType: lane.laneType,
                genre: lane.genre,
                subgenre: lane.subgenre,
                label: lane.subgenre || lane.genre,
                entries
            }];
    });
}
export function buildPrimePicksPanelSlots(category) {
    const entriesByRank = new Map();
    for (const entry of category?.entries || []) {
        if (!entriesByRank.has(entry.rank))
            entriesByRank.set(entry.rank, entry);
    }
    return [1, 2, 3].map((rank) => entriesByRank.get(rank) || null);
}
